import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { buildUrl, ENDPOINTS } from '../config';
import './FieldManagement.css';

function createEmptyFieldData() {
    return {
        name: '',
        label: '',
        type: 'string',
        required: false,
        primaryKey: false,
        placeholder: '',
        showInList: true,
        editable: true,
        displayOrder: 0,
        validation: '',
        options: '',
        defaultValue: ''
    };
}

function parseOptionalJson(value, label) {
    if (!value || !String(value).trim()) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        throw new Error(`${label} 不是合法的 JSON。`);
    }
}

function FieldManagement() {
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingField, setEditingField] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState(createEmptyFieldData());

    const fieldTypes = [
        { value: 'string', label: '单行文本' },
        { value: 'text', label: '多行文本' },
        { value: 'number', label: '整数' },
        { value: 'decimal', label: '小数' },
        { value: 'date', label: '日期' },
        { value: 'datetime', label: '日期时间' },
        { value: 'boolean', label: '布尔值' },
        { value: 'select', label: '下拉选择' }
    ];

    useEffect(() => {
        loadFields();
    }, []);

    const sortedFields = useMemo(
        () => [...fields].sort((fieldA, fieldB) => (fieldA.displayOrder || 0) - (fieldB.displayOrder || 0)),
        [fields]
    );

    const unmigratedCount = useMemo(
        () => sortedFields.filter((field) => !field.isMigrated).length,
        [sortedFields]
    );

    const loadFields = async () => {
        try {
            setLoading(true);
            const response = await axios.get(buildUrl(ENDPOINTS.fields));
            setFields(response.data);
        } catch (requestError) {
            console.error('加载字段配置失败:', requestError);
            alert('加载字段配置失败，请稍后重试。');
        } finally {
            setLoading(false);
        }
    };

    const resetFormState = () => {
        setEditingField(null);
        setShowAddForm(false);
        setFormData(createEmptyFieldData());
    };

    const preparePayload = () => ({
        ...formData,
        validation: parseOptionalJson(formData.validation, '校验规则'),
        options: parseOptionalJson(formData.options, '下拉选项'),
        displayOrder: parseInt(formData.displayOrder, 10) || 0
    });

    const handleAddField = async () => {
        try {
            if (!formData.name.trim() || !formData.label.trim()) {
                alert('字段名和显示标签为必填项。');
                return;
            }

            await axios.post(buildUrl(ENDPOINTS.fields), preparePayload());
            alert('字段已添加。');
            resetFormState();
            loadFields();
        } catch (requestError) {
            console.error('添加字段失败:', requestError);
            alert(`添加字段失败：${requestError.response?.data?.error || requestError.message}`);
        }
    };

    const handleUpdateField = async () => {
        try {
            if (!editingField) {
                return;
            }

            await axios.put(buildUrl(`/api/fields/${editingField.name}`), preparePayload());
            alert('字段已更新。');
            resetFormState();
            loadFields();
        } catch (requestError) {
            console.error('更新字段失败:', requestError);
            alert(`更新字段失败：${requestError.response?.data?.error || requestError.message}`);
        }
    };

    const handleDeleteField = async (fieldName) => {
        if (!window.confirm(`确定要删除字段配置“${fieldName}”吗？数据库列和历史数据不会被删除。`)) {
            return;
        }

        try {
            await axios.delete(buildUrl(`/api/fields/${fieldName}`));
            alert('字段配置已删除。');
            loadFields();
        } catch (requestError) {
            console.error('删除字段失败:', requestError);
            alert(`删除字段失败：${requestError.response?.data?.error || requestError.message}`);
        }
    };

    const handleMigrateField = async (fieldName) => {
        if (!window.confirm(`确定要将字段“${fieldName}”应用到数据库吗？这会尝试向 elevators 表新增对应列。`)) {
            return;
        }

        try {
            const response = await axios.post(buildUrl(`/api/fields/${fieldName}/migrate`));
            alert(response.data.message || '字段已应用到数据库。');
            loadFields();
        } catch (requestError) {
            console.error('字段迁移失败:', requestError);
            alert(`字段迁移失败：${requestError.response?.data?.error || requestError.message}`);
        }
    };

    const handleBatchMigrate = async () => {
        if (!window.confirm('确定要批量应用所有未迁移字段吗？该操作会逐项尝试更新数据库结构。')) {
            return;
        }

        try {
            const response = await axios.post(buildUrl('/api/fields/batch-migrate'));
            alert(response.data.message || '批量迁移已完成。');
            loadFields();
        } catch (requestError) {
            console.error('批量迁移失败:', requestError);
            alert(`批量迁移失败：${requestError.response?.data?.error || requestError.message}`);
        }
    };

    const startEdit = (field) => {
        setEditingField(field);
        setShowAddForm(false);
        setFormData({
            ...field,
            validation: field.validation ? JSON.stringify(field.validation, null, 2) : '',
            options: field.options ? JSON.stringify(field.options, null, 2) : ''
        });
    };

    if (loading) {
        return (
            <div className="field-layout">
                <div className="app-panel app-message-card">
                    <h2>正在加载字段配置...</h2>
                    <p>请稍候，系统正在读取当前配置。</p>
                </div>
            </div>
        );
    }

    return (
        <section className="field-layout">
            <div className="field-overview app-panel">
                <div className="page-heading">
                    <div>
                        <span className="app-eyebrow">字段配置</span>
                        <h2>展示字段与录入规则</h2>
                        <p>这里控制前端表单、列表列和校验规则。数据库迁移操作仍建议谨慎执行。</p>
                    </div>
                    <div className="field-overview__actions">
                        <button
                            type="button"
                            className="app-button app-button--primary"
                            onClick={() => {
                                setShowAddForm(true);
                                setEditingField(null);
                                setFormData(createEmptyFieldData());
                            }}
                        >
                            新增字段
                        </button>
                        <button type="button" className="app-button app-button--warning" onClick={handleBatchMigrate}>
                            批量应用未迁移字段
                        </button>
                    </div>
                </div>

                <div className="field-overview__meta">
                    <span className="app-badge">{sortedFields.length} 个字段</span>
                    <span className="app-badge app-badge--warning">{unmigratedCount} 个未迁移</span>
                </div>
            </div>

            {(showAddForm || editingField) && (
                <div className="field-form-card app-panel app-panel--strong">
                    <div className="panel-heading">
                        <h3>{editingField ? `编辑字段：${editingField.label}` : '新增字段'}</h3>
                        <p>字段配置只影响前端展示和接口字段映射，真正落库仍需要手动执行迁移。</p>
                    </div>

                    <div className="field-form-section">
                        <h4>基础信息</h4>
                        <div className="field-form-grid">
                            <label className="field-form-group">
                                <span>字段名 *</span>
                                <input
                                    type="text"
                                    className="app-input"
                                    value={formData.name}
                                    onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                                    placeholder="例如：note"
                                    disabled={Boolean(editingField)}
                                />
                                <small>建议仅使用字母、数字和下划线。</small>
                            </label>

                            <label className="field-form-group">
                                <span>显示标签 *</span>
                                <input
                                    type="text"
                                    className="app-input"
                                    value={formData.label}
                                    onChange={(event) => setFormData({ ...formData, label: event.target.value })}
                                    placeholder="例如：备注"
                                />
                            </label>

                            <label className="field-form-group">
                                <span>字段类型</span>
                                <select
                                    className="app-select"
                                    value={formData.type}
                                    onChange={(event) => setFormData({ ...formData, type: event.target.value })}
                                >
                                    {fieldTypes.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="field-form-group">
                                <span>显示顺序</span>
                                <input
                                    type="number"
                                    className="app-input"
                                    value={formData.displayOrder}
                                    onChange={(event) => setFormData({ ...formData, displayOrder: event.target.value })}
                                />
                            </label>

                            <label className="field-form-group field-form-group--full">
                                <span>输入提示</span>
                                <input
                                    type="text"
                                    className="app-input"
                                    value={formData.placeholder}
                                    onChange={(event) => setFormData({ ...formData, placeholder: event.target.value })}
                                    placeholder="表单输入框提示文本"
                                />
                            </label>

                            <label className="field-form-group">
                                <span>默认值</span>
                                <input
                                    type="text"
                                    className="app-input"
                                    value={formData.defaultValue}
                                    onChange={(event) => setFormData({ ...formData, defaultValue: event.target.value })}
                                    placeholder="可选"
                                />
                            </label>
                        </div>
                    </div>

                    <div className="field-form-section">
                        <h4>高级配置</h4>
                        <div className="field-form-grid">
                            <label className="field-form-group field-form-group--full">
                                <span>校验规则（JSON）</span>
                                <textarea
                                    className="app-textarea"
                                    value={formData.validation}
                                    onChange={(event) => setFormData({ ...formData, validation: event.target.value })}
                                    placeholder={'例如：{"pattern":"^\\\\d+$","message":"请输入数字"}'}
                                />
                                <small>可选字段包括 pattern、message、min、max。</small>
                            </label>

                            <label className="field-form-group field-form-group--full">
                                <span>下拉选项（JSON）</span>
                                <textarea
                                    className="app-textarea"
                                    value={formData.options}
                                    onChange={(event) => setFormData({ ...formData, options: event.target.value })}
                                    placeholder={'例如：[{"label":"住宅","value":"residential"}]'}
                                />
                                <small>仅在下拉字段类型下使用，格式为对象数组。</small>
                            </label>
                        </div>
                    </div>

                    <div className="field-form-section">
                        <h4>展示行为</h4>
                        <div className="field-toggle-grid">
                            <label className="field-toggle">
                                <input
                                    type="checkbox"
                                    checked={formData.required}
                                    onChange={(event) => setFormData({ ...formData, required: event.target.checked })}
                                />
                                <span>设为必填字段</span>
                            </label>

                            <label className="field-toggle">
                                <input
                                    type="checkbox"
                                    checked={formData.showInList}
                                    onChange={(event) => setFormData({ ...formData, showInList: event.target.checked })}
                                />
                                <span>在列表中显示</span>
                            </label>

                            <label className="field-toggle">
                                <input
                                    type="checkbox"
                                    checked={formData.editable}
                                    onChange={(event) => setFormData({ ...formData, editable: event.target.checked })}
                                />
                                <span>允许编辑</span>
                            </label>

                            <label className="field-toggle">
                                <input
                                    type="checkbox"
                                    checked={formData.primaryKey}
                                    onChange={(event) => setFormData({ ...formData, primaryKey: event.target.checked })}
                                    disabled={Boolean(editingField)}
                                />
                                <span>设为主键</span>
                            </label>
                        </div>
                    </div>

                    <div className="field-form-actions">
                        <button
                            type="button"
                            className="app-button app-button--primary"
                            onClick={editingField ? handleUpdateField : handleAddField}
                        >
                            {editingField ? '保存修改' : '创建字段'}
                        </button>
                        <button type="button" className="app-button app-button--secondary" onClick={resetFormState}>
                            取消
                        </button>
                    </div>
                </div>
            )}

            <div className="field-list-card app-panel app-panel--strong">
                <div className="field-list-card__header">
                    <div>
                        <h3>当前字段配置</h3>
                        <p>每张卡片展示字段类型、显示策略和数据库迁移状态。</p>
                    </div>
                </div>

                <div className="field-grid">
                    {sortedFields.map((field) => (
                        <article key={field.name} className="field-card">
                            <div className="field-card__header">
                                <div>
                                    <h4>{field.label}</h4>
                                    <code>{field.name}</code>
                                </div>
                                <span className={`app-badge ${field.isMigrated ? 'app-badge--success' : 'app-badge--warning'}`}>
                                    {field.isMigrated ? '已迁移' : '未迁移'}
                                </span>
                            </div>

                            <div className="field-card__meta">
                                <span className="app-badge">{fieldTypes.find((type) => type.value === field.type)?.label || field.type}</span>
                                <span className="app-badge">顺序 {field.displayOrder || 0}</span>
                            </div>

                            <div className="field-card__tags">
                                {field.required && <span className="app-badge app-badge--danger">必填</span>}
                                {field.primaryKey && <span className="app-badge app-badge--warning">主键</span>}
                                {field.showInList && <span className="app-badge app-badge--success">列表显示</span>}
                                {!field.editable && <span className="app-badge">只读</span>}
                            </div>

                            {!field.isMigrated && (
                                <div className="field-card__warning">
                                    此字段尚未写入数据库结构，目前只能作为配置存在，不能稳定用于正式录入。
                                </div>
                            )}

                            <div className="field-card__actions">
                                <button type="button" className="app-button app-button--ghost app-button--small" onClick={() => startEdit(field)}>
                                    编辑
                                </button>
                                <button
                                    type="button"
                                    className="app-button app-button--warning app-button--small"
                                    onClick={() => handleMigrateField(field.name)}
                                >
                                    应用到数据库
                                </button>
                                {!field.primaryKey && (
                                    <button
                                        type="button"
                                        className="app-button app-button--danger app-button--small"
                                        onClick={() => handleDeleteField(field.name)}
                                    >
                                        删除配置
                                    </button>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default FieldManagement;
